import { PETE_PARAMETER_LENGTH_LIMIT } from "@faire/web--source/common/consts/PETE_CONSTANTS";
import { getGlobalProperty } from "@faire/web--source/common/globals/getGlobalProperty";
import { getWindowOrThrow } from "@faire/web--source/common/globals/getWindow";
import { getWindow } from "@faire/web--source/common/globals/getWindow";
import { logError } from "@faire/web--source/common/logging";
import { getCurrentPath } from "@faire/web--source/common/Path";
import { HistoryTiming } from "@faire/web--source/common/performance/HistoryTiming";
import { trackPerformanceBreakdownInUserPerceivedLoadTiming } from "@faire/web--source/common/performance/TrackPerformanceBreakdown/integration/trackPerformanceBreakdownInUserPerceivedLoadTiming";
import { ITraceEventMarker } from "@faire/web--source/common/pete/TraceEventTracker";
import { IManualEventOverrides } from "@faire/web--source/common/pete/types";
import { theFuture } from "@faire/web--source/common/theFuture";
import { trackInfraUpltTimingTracePageLoaded } from "@faire/web-api--source/events/infra/timing/uplt";
import { trackInfraUpltTimingTracePageReady } from "@faire/web-api--source/events/infra/timing/uplt";
import { trackInfraUpltTiming } from "@faire/web-api--source/events/infra/timing/uplt";
import {
  trackInfraUserPerceivedLoadTimingTiming,
  trackInfraUserPerceivedLoadTimingTimingTraceMeasurementcompleted,
  trackInfraUserPerceivedLoadTimingTimingTracePageready,
} from "@faire/web-api--source/events/infra/timing/userPerceivedLoadTiming";
import { trackInfraUalEventView } from "@faire/web-api--source/events/infra/view/ualEvent";
import { Optional } from "utility-types";

import { TelemetryManager } from "../../opentelemetry/TelemetryManager";
import { detectQuietMainThread } from "../detectQuietMainThread";
import { trackPageWeight } from "../trackPageWeight";
import { getGlobalPerformanceMarks } from "../TrackPerformanceBreakdown/getGlobalPerformanceMarks";
import { PerformanceMarkStartTime } from "../TrackPerformanceBreakdown/markPageJsExecuted";
import { PerformanceBreakdown } from "../TrackPerformanceBreakdown/PerformanceBreakdown";
import { trackQuietMainThread } from "../trackQuietMainThread";

import { AbortNotifier, ReadonlyAbortNotifier } from "./AbortNotifier";
import { ActionStartTimeStore } from "./ActionStartTimeStore";
import { PageLoadTracker } from "./PageLoadTracker";
import {
  LoadTimingDebugInfo,
  LoadTimingMetaInfo,
  LoadTimingOptions,
  LoadTimingResult,
  LoadTimingStatus,
  LoadTimingType,
  RefCallbackOptions,
  UserPerceivedLoadFailureType,
} from "./types";
import { waitForImagesToLoad } from "./waitForImagesToLoad";

const ONE_HOUR_IN_MS = 3600_000;
const DEFAULT_OPTIONS: Required<
  Omit<LoadTimingOptions, "name" | "variant" | "pageLoadKey">
> = {
  numComponents: 1,
  ignoreImages: false,
  waitForPageLoadTimeoutMs: 30_000,
  waitForComponentMountTimeoutMs: 5_000,
  actionStartTime: 0,
  enableDebugLogging: false,
  enableTrackPerformanceBreakdown: false,
  enablePageUpdateFix: false,
  trackResultCallback: () => {},
  mainThreadMaxWaitTimeMs: 500,
  mainThreadFrameLatencyThresholdMs: 50,
  mainThreadFrameLatencyThresholdMsV1_1: 250,
  appName: "unknown",
};

export type LoadTimingRef = (
  element: HTMLElement | null,
  options?: RefCallbackOptions
) => void;

export class UserPerceivedLoadTiming {
  private _options: Optional<
    Required<LoadTimingOptions>,
    "variant" | "pageLoadKey"
  >;
  private targetElements: Array<HTMLElement> = [];
  private isActionReady = false;
  private hasMountTimerExpired = false;
  private sendResultPromise: Promise<void> | undefined;
  private notifier = new AbortNotifier<LoadTimingStatus>();
  private error: UserPerceivedLoadFailureType | undefined = undefined;

  private initialPath: string | undefined;
  private actionStartTime: number = 0;
  private actionReadyTime: number = 0;
  loadType: LoadTimingType | undefined;

  pageMountTime: number | undefined = undefined;
  private readyToFetchDataTime: number | undefined = undefined;
  private dataFetchedTime: number | undefined = undefined;
  private renderedWithDataTime: number | undefined = undefined;
  private pageJsExecutedTime: number | undefined = undefined;
  private globalPerformanceMarks:
    | Pick<PerformanceBreakdown, "htmlStarted" | "mainJsExecuted">
    | undefined = undefined;

  private debugInstanceId = Math.round(Math.random() * 1000);

  hasCompleted: boolean = false;
  constructor(
    options: Optional<LoadTimingOptions, "pageLoadKey">,
    timings?: {
      pageMountTime: number | undefined;
      dataUpdatedAt: number | undefined;
      pageJsExecutedMark: PerformanceMarkStartTime | undefined;
    }
  ) {
    this._options = { ...DEFAULT_OPTIONS, ...options };
    this.pageMountTime = timings?.pageMountTime;
    this.setDataUpdatedAt(timings?.dataUpdatedAt);
    this.setPageJsExecutedMark(timings?.pageJsExecutedMark);

    if (this.options.enablePageUpdateFix === true) {
      if (options.actionStartTime) {
        this.loadType = "ACTION";
        this.actionStartTime = options.actionStartTime;
        this.clearActionStartTime();
      } else {
        const { timeOfLastRouteChange } = HistoryTiming.get();

        this.actionStartTime = timeOfLastRouteChange;
        this.loadType = getPageLoadTypeUsingPageJsTime(
          timeOfLastRouteChange,
          this.pageMountTime,
          this.pageJsExecutedTime
        );
      }
    }
    const startLoadTimeInteractionTracking = getGlobalProperty(
      "startLoadTimeInteractionTracking"
    ) as (() => void) | undefined;
    if (startLoadTimeInteractionTracking) {
      startLoadTimeInteractionTracking();
    }
  }

  get options() {
    return this._options;
  }

  start = (optionsOverride?: Partial<LoadTimingOptions>) => {
    if (this.isActionReady) {
      return;
    }

    this.debugLog("started");

    if (optionsOverride) {
      this._options = {
        ...this._options,
        ...optionsOverride,
      };
    }

    const { document, performance } = getWindowOrThrow();

    this.isActionReady = true;
    this.actionReadyTime = performance.now();
    this.initialPath = getCurrentPath().toString();

    if (this.loadType === undefined && this.options.actionStartTime) {
      this.loadType = "ACTION";
      this.actionStartTime = this.options.actionStartTime;
      this.clearActionStartTime();
    } else if (this.loadType === undefined) {
      const timeOfLastRouteChange =
        this.options.enablePageUpdateFix === "v2"
          ? HistoryTiming.get().getTimeOfLastRouteChangeFromEventTimestamps()
          : HistoryTiming.get().timeOfLastRouteChange;

      this.actionStartTime = timeOfLastRouteChange;
      this.loadType = getPageLoadType(
        timeOfLastRouteChange,
        this.pageMountTime,
        PageLoadTracker.get().wasPageLoaded(this.options.name)
      );
    }

    if (this.loadType !== "ACTION") {
      PageLoadTracker.get().setPageLoaded(this.options.name);

      this.globalPerformanceMarks = getGlobalPerformanceMarks();

      trackPageWeight(
        this.initialPath,
        this.options.name,
        this.loadType,
        this.actionStartTime
      );
      if (document.visibilityState === "hidden") {
        this.trackResultV1_1({
          status: "PAGE_STARTED_HIDDEN",
        });
        this.trackResult({
          status: "PAGE_STARTED_HIDDEN",
        });
        this.sendResultPromise = Promise.resolve();
        return;
      }
    }

    const timeoutId = setTimeout(() => {
      this.hasMountTimerExpired = true;
      this.startMeasurementIfReady();
    }, this.options.waitForComponentMountTimeoutMs);

    this.notifier.addAbortListener(() => {
      clearTimeout(timeoutId);
    });

    this.startMeasurementIfReady();
  };

  cancel = () => {
    if (!this.isActionReady) {
      return;
    }

    this.targetElements = [];
    this.clearActionStartTime();
    this.notifier.abort("CANCELLED");

    this.debugLog("cancelled");
  };

  private debugLog = (...args: Array<unknown>) => {
    if (this.options.enableDebugLogging) {
      // eslint-disable-next-line no-console
      console.log("UPLT", this.debugInstanceId, ...args);
    }
  };

  private clearActionStartTime = () => {
    ActionStartTimeStore.get().cleanActionStartTime(this.options.name);
  };

  get hasStarted() {
    return this.isActionReady;
  }

  notifyPageMount = () => {
    this.pageMountTime = getWindowOrThrow().performance.now();
  };

  notifyPageUnmount = () => {
    this.notifier.abort("PARENT_COMPONENT_UNMOUNTED");
  };

  notifyReadyToFetchData = () => {
    this.readyToFetchDataTime = getWindowOrThrow().performance.now();
  };

  notifyRenderedWithData = () => {
    this.renderedWithDataTime = getWindowOrThrow().performance.now();
  };

  setDataUpdatedAt = (dataUpdatedAt: number | undefined) => {
    // `dataUpdatedAt` comes from Date.now() via useQuery(). It relative to the UNIX epoch.
    // UPLT uses timestamps returned by performance.now(). They are relative to `timeOrigin`.
    this.dataFetchedTime = timeRelativeToTimeOrigin(dataUpdatedAt);
  };

  setPageJsExecutedMark = (mark: PerformanceMarkStartTime | undefined) => {
    this.pageJsExecutedTime = mark?.startTime;
  };

  setIgnoreImages = (ignoreImages: boolean) => {
    this._options = {
      ...this._options,
      ignoreImages,
    };
  };

  setNumComponents = (
    numComponents: number = DEFAULT_OPTIONS.numComponents
  ) => {
    this._options = {
      ...this._options,
      numComponents,
    };
    this.startMeasurementIfReady();
  };

  readonly refCallbackWithOptions: LoadTimingRef = (
    element: HTMLElement | null,
    options?: RefCallbackOptions
  ) => {
    if (options?.status === "error" && options?.error) {
      this.error = options.error;
      this.notifier.abort("ERROR_SHOWN");
    }

    if (
      options?.pageLoadKey !== undefined &&
      this._options.pageLoadKey !== options.pageLoadKey
    ) {
      this.debugLog(
        "refCallbackWithOptions ignored with pageLoadKey mismatch",
        element
      );
      return;
    }

    if (element) {
      this.targetElements.push(element);
      this.startMeasurementIfReady();
    } else {
      this.targetElements = [];
      this.notifier.abort("TARGET_COMPONENT_UNMOUNTED");
    }

    this.debugLog(
      "refCallbackWithOptions called" +
        (options?.pageLoadKey
          ? ` with pageLoadKey: ${options?.pageLoadKey}`
          : ""),
      element
    );
  };

  readonly refCallback: React.RefCallback<HTMLElement> = (element) => {
    if (element) {
      this.targetElements.push(element);
      this.startMeasurementIfReady();
    } else {
      this.targetElements = [];
      this.notifier.abort("TARGET_COMPONENT_UNMOUNTED");
    }

    this.debugLog("refCallback called", element);
  };

  private startMeasurementIfReady = () => {
    if (this.sendResultPromise) {
      return;
    }

    const componentsMissing =
      this.targetElements.length < this.options.numComponents;
    const waitingForComponents =
      !this.hasMountTimerExpired && componentsMissing;

    if (this.notifier.aborted) {
      this.trackResultV1_1({
        status: this.notifier.reason || "UNKNOWN_ERROR",
      });
      this.trackResult({
        status: this.notifier.reason || "UNKNOWN_ERROR",
      });
      this.sendResultPromise = Promise.resolve();
    } else if (waitingForComponents || !this.isActionReady) {
      return;
    } else if (componentsMissing) {
      this.trackResultV1_1({
        status: "TARGET_COMPONENT_MISSING",
      });
      this.trackResult({
        status: "TARGET_COMPONENT_MISSING",
      });
      this.sendResultPromise = Promise.resolve();
    } else {
      theFuture(this.options.waitForPageLoadTimeoutMs).then(() => {
        this.notifier.abort("TIMEOUT");
      });

      trackQuietMainThread(this.options.name, this.loadType, this.notifier);

      const measurementV1_1 = startMeasurement(
        this.targetElements,
        this.notifier,
        this.options.ignoreImages,
        this.options.mainThreadMaxWaitTimeMs,
        this.options.mainThreadFrameLatencyThresholdMsV1_1
      )
        .catch(
          () =>
            ({
              status: this.notifier.reason || "UNKNOWN_ERROR",
            }) as MeasurementResult
        )
        .then(this.trackResultV1_1);

      const measurementV1 = startMeasurement(
        this.targetElements,
        this.notifier,
        this.options.ignoreImages,
        this.options.mainThreadMaxWaitTimeMs,
        this.options.mainThreadFrameLatencyThresholdMs
      )
        .catch(
          () =>
            ({
              status: this.notifier.reason || "UNKNOWN_ERROR",
            }) as MeasurementResult
        )
        .then(this.trackResult);

      this.sendResultPromise = Promise.all([
        measurementV1_1,
        measurementV1,
      ]).then(() => {
        // Trigger all abort listeners, to clean up any remaining listeners.
        this.notifier.abort("CLEAN_UP");
        this.hasCompleted = true;
      });
    }
  };

  private getTimeDeltaWithClampMax(
    time: DOMHighResTimeStamp
  ): DOMHighResTimeStamp;
  private getTimeDeltaWithClampMax(
    time: DOMHighResTimeStamp | null
  ): DOMHighResTimeStamp | null;
  // eslint-disable-next-line @faire/prefer-arrow-methods
  private getTimeDeltaWithClampMax(time: DOMHighResTimeStamp | null) {
    if (time == null || time === 0) {
      return time;
    } else {
      // Cap the time to 1 hour
      return Math.min(Math.round(time - this.actionStartTime), ONE_HOUR_IN_MS);
    }
  }

  private trackResultV1_1 = (result: MeasurementResult) => {
    const { performance } = getWindowOrThrow();

    const { timeOfLastRouteChange } = HistoryTiming.get();
    const hasPageChanged =
      this.actionStartTime < timeOfLastRouteChange &&
      /* The last route change might have changed part of the URL,
       * but stayed on the same page (eg. opening a modal).
       * Let's only consider it a navigation, if the page component
       * or some of the content components unmount.
       */
      (this.notifier.reason === "PARENT_COMPONENT_UNMOUNTED" ||
        this.notifier.reason === "TARGET_COMPONENT_UNMOUNTED");

    let endTime: DOMHighResTimeStamp;
    if (hasPageChanged) {
      endTime = timeOfLastRouteChange;
    } else if (result.mainThreadQuietTime) {
      endTime = Math.max(
        result.mainThreadQuietTime,
        result.lastImageLoadTime ?? 0
      );
    } else {
      endTime = performance.now();
    }

    const metaInfoPayLoad: LoadTimingMetaInfo = {
      name: this.options.name,
      variant: this.options.variant,
      isInitialLoad: this.actionStartTime === 0,
      loadType: this.loadType,
      isSSR: getGlobalProperty("needsHydration", false),
    };

    const relativeEndTime = this.getTimeDeltaWithClampMax(endTime);
    let status = result.status;
    if (status === "ERROR_SHOWN") {
      // If the status is already a failure, we don't want to change the status
    } else if (hasPageChanged) {
      status = "PAGE_NAVIGATED";
    } else if (relativeEndTime < 0) {
      status = "NEGATIVE_END_TIME";
    }

    const resultPayload: LoadTimingResult = {
      status: status,
      pageMountTime: this.getTimeDeltaWithClampMax(this.pageMountTime ?? null),
      pageReadyTime: this.getTimeDeltaWithClampMax(this.actionReadyTime),
      mainThreadQuietTime: this.getTimeDeltaWithClampMax(
        result.mainThreadQuietTime ?? null
      ),
      lastImageLoadTime: this.getTimeDeltaWithClampMax(
        result.lastImageLoadTime ?? null
      ),
      endTime: relativeEndTime,
    };

    const marker = trackInfraUpltTiming(
      this.options.name,
      metaInfoPayLoad.loadType ?? "",
      resultPayload.status,
      { timestampOverride: this.actionStartTime }
    );

    trackInfraUpltTimingTracePageReady(
      this.options.name,
      this.loadType ?? "",
      status,
      TelemetryManager.get().getActiveTraceId() ?? "",
      marker,
      {
        timestampOverride: this.actionReadyTime,
      }
    );

    trackInfraUpltTimingTracePageLoaded(
      this.options.name,
      this.loadType ?? "",
      status,
      TelemetryManager.get().getActiveTraceId() ?? "",
      this.error ?? "",
      marker,
      { timestampOverride: endTime }
    );
  };

  private trackResult = (result: MeasurementResult) => {
    const { performance } = getWindowOrThrow();

    const { timeOfLastRouteChange } = HistoryTiming.get();
    const hasPageChanged =
      this.actionStartTime < timeOfLastRouteChange &&
      /* The last route change might have changed part of the URL,
       * but stayed on the same page (eg. opening a modal).
       * Let's only consider it a navigation, if the page component
       * or some of the content components unmount.
       */
      (this.notifier.reason === "PARENT_COMPONENT_UNMOUNTED" ||
        this.notifier.reason === "TARGET_COMPONENT_UNMOUNTED");

    let endTime: DOMHighResTimeStamp;
    if (hasPageChanged) {
      endTime = timeOfLastRouteChange;
    } else if (result.mainThreadQuietTime) {
      endTime = Math.max(
        result.mainThreadQuietTime,
        result.lastImageLoadTime ?? 0
      );
    } else {
      endTime = performance.now();
    }

    const isNextJS = getGlobalProperty("isNextJS", false);

    const metaInfoPayLoad: LoadTimingMetaInfo = {
      name: this.options.name,
      variant: this.options.variant,
      isInitialLoad: this.actionStartTime === 0,
      loadType: this.loadType,
      isSSR: getGlobalProperty("needsHydration", false),
      isNextJS: isNextJS,
    };

    const relativeEndTime = this.getTimeDeltaWithClampMax(endTime);
    let status = result.status;
    if (hasPageChanged) {
      status = "PAGE_NAVIGATED";
    } else if (relativeEndTime < 0) {
      status = "NEGATIVE_END_TIME";
    }

    const resultPayload: LoadTimingResult = {
      status: status,
      pageMountTime: this.getTimeDeltaWithClampMax(this.pageMountTime ?? null),
      pageReadyTime: this.getTimeDeltaWithClampMax(this.actionReadyTime),
      mainThreadQuietTime: this.getTimeDeltaWithClampMax(
        result.mainThreadQuietTime ?? null
      ),
      lastImageLoadTime: this.getTimeDeltaWithClampMax(
        result.lastImageLoadTime ?? null
      ),
      endTime: relativeEndTime,
    };

    const debugPayload: LoadTimingDebugInfo = {
      numOfElements: this.targetElements.length,
      numOfImages: result.numOfImages ?? 0,
      path: undefined,
      templatedPath: getGlobalProperty("templatedPath"),
    };

    const marker = trackInfraUserPerceivedLoadTimingTiming(
      this.options.name,
      metaInfoPayLoad.loadType ?? "",
      resultPayload.status,
      { timestampOverride: this.actionStartTime }
    );
    this.trackTrace(
      status,
      marker,
      isNextJS,
      this.options.appName,
      trackInfraUserPerceivedLoadTimingTimingTracePageready,
      endTime
    );
    this.trackTrace(
      status,
      marker,
      isNextJS,
      this.options.appName,
      trackInfraUserPerceivedLoadTimingTimingTraceMeasurementcompleted,
      endTime
    );

    const metaInfoPayLoadString = JSON.stringify(metaInfoPayLoad);
    const resultPayloadString = JSON.stringify(resultPayload);
    const debugPayLoadString = JSON.stringify(debugPayload);
    if (
      metaInfoPayLoadString.length > PETE_PARAMETER_LENGTH_LIMIT ||
      resultPayloadString.length > PETE_PARAMETER_LENGTH_LIMIT ||
      debugPayLoadString.length > PETE_PARAMETER_LENGTH_LIMIT
    ) {
      logError(
        new Error(
          `UPLT result payloads exceeded the ${PETE_PARAMETER_LENGTH_LIMIT} length limit.
          ${metaInfoPayLoadString},
          ${resultPayloadString},
          ${debugPayLoadString}`
        )
      );
    }
    trackInfraUalEventView(
      JSON.stringify(metaInfoPayLoad),
      JSON.stringify(resultPayload),
      JSON.stringify(debugPayload)
    );

    this.debugLog("finished", metaInfoPayLoad, resultPayload, debugPayload);

    this.trackPerformanceBreakdown(status, result, endTime);
    this.options.trackResultCallback?.(
      this.options.name,
      status,
      this.loadType,
      resultPayload
    );
  };

  trackPerformanceBreakdown = (
    status: LoadTimingStatus,
    result: MeasurementResult,
    endTime: number
  ) => {
    if (
      this.options.enableTrackPerformanceBreakdown === "internal-tracking" &&
      this.globalPerformanceMarks
    ) {
      /**
       * In case of a useQuery cache hit or prefetch, `dataFetchedTime`
       * may be before `actionStartTime` or `pageMountTime`.
       * In the case that the page does not have a data fetch
       * (ie. logged-out home), `dataFetchedTime` may be `undefined`.
       *
       * To avoid a negative segment duration, the value should not be less
       * than the end time of the previous segment.
       */
      const dataFetched = Math.max(
        this.dataFetchedTime ?? 0,
        this.actionStartTime,
        this.pageMountTime ?? 0
      );

      // In case of a useQuery cache hit or prefetch, `readyToFetchDataTime`
      // is not relevant (because there is no fetch about to begin).
      // The DATA segment duration should be zero if the `dataFetchedTime`
      // already happened before the component was ready.
      const readyToFetchData = Math.min(
        dataFetched,
        this.readyToFetchDataTime ?? 0
      );

      const breakdown: PerformanceBreakdown = {
        htmlStarted: this.globalPerformanceMarks?.htmlStarted,
        mainJsExecuted: this.globalPerformanceMarks?.mainJsExecuted,
        pageComponentMounted: this.pageMountTime,
        readyToFetchData,
        dataFetched,
        pageComponentRenderedWithData: this.renderedWithDataTime,
        // readyToFetchPageJs is deprecated and not used
        readyToFetchPageJs: undefined,
        pageJsExecuted: Math.max(
          this.actionStartTime,
          this.pageJsExecutedTime ?? 0
        ),
      };

      trackPerformanceBreakdownInUserPerceivedLoadTiming({
        name: this.options.name,
        loadType: this.loadType,
        status: status,
        timeOfLastRouteChange: this.actionStartTime,
        breakdown,
        lastImageLoadTime: result.lastImageLoadTime,
        mainThreadQuietTime: result.mainThreadQuietTime,
        upltEndTime: endTime,
      });
    }
  };

  private trackTrace = (
    status: LoadTimingStatus,
    marker: ITraceEventMarker,
    isNextJS: boolean,
    appName: string,
    traceFunction: (
      name: string,
      loadType: string,
      status: string,
      isNextJS: string,
      traceId: string,
      appName: string,
      sourceEventMarker?: ITraceEventMarker,
      manualOverrides?: IManualEventOverrides
    ) => void,
    time: DOMHighResTimeStamp
  ) => {
    traceFunction(
      this.options.name,
      this.loadType ?? "",
      status,
      `${isNextJS}`,
      TelemetryManager.get().getActiveTraceId() ?? "",
      appName,
      marker,
      {
        timestampOverride: time,
      }
    );
  };
}

interface MeasurementResult {
  status: LoadTimingStatus;
  mainThreadQuietTime?: DOMHighResTimeStamp;
  lastImageLoadTime?: DOMHighResTimeStamp;
  numOfImages?: number;
}

const startMeasurement = (
  targetElements: Array<HTMLElement>,
  notifier: ReadonlyAbortNotifier<LoadTimingStatus>,
  ignoreImages: boolean,
  maxQuietWindowWaitTimeMs: number,
  frameLatencyThresholdMs: number
): Promise<MeasurementResult> =>
  Promise.all([
    detectQuietMainThread(
      maxQuietWindowWaitTimeMs,
      frameLatencyThresholdMs,
      notifier
    ),
    ignoreImages
      ? Promise.resolve(null)
      : waitForImagesToLoad(targetElements, notifier),
  ]).then(([quietMainThreadResult, imageLoadResult]) => {
    let status: LoadTimingStatus;

    if (notifier.aborted) {
      status = notifier.reason || "UNKNOWN_ERROR";
    } else if (quietMainThreadResult.status === "page-hidden") {
      status = "PAGE_TRANSITIONED_TO_HIDDEN";
    } else {
      const zeroImages =
        imageLoadResult == null || imageLoadResult.numImages === 0;
      if (zeroImages && !ignoreImages) {
        status = "IMAGES_MISSING";
      } else {
        status = "SUCCESS";
      }
    }

    return {
      status,
      mainThreadQuietTime: quietMainThreadResult?.startOfQuietWindow,
      lastImageLoadTime: imageLoadResult?.lastImageLoadTime,
      numOfImages: imageLoadResult?.numImages || 0,
    };
  });

const getPageLoadType = (
  timeOfLastRouteChange: number,
  pageMountTime: number | undefined,
  wasPageLoaded: boolean
): LoadTimingType => {
  if (pageMountTime !== undefined && pageMountTime < timeOfLastRouteChange) {
    return "PAGE_UPDATE";
  } else if (timeOfLastRouteChange > 0) {
    return wasPageLoaded ? "ROUTE_CHANGE_SUBSEQUENT" : "ROUTE_CHANGE_FIRST";
  } else {
    return "DIRECT_LOAD";
  }
};

const getPageLoadTypeUsingPageJsTime = (
  timeOfLastRouteChange: number,
  pageMountTime: number | undefined,
  pageJsExecutedTime: number | undefined
): LoadTimingType => {
  if (timeOfLastRouteChange > 0) {
    if (pageMountTime !== undefined && pageMountTime < timeOfLastRouteChange) {
      return "PAGE_UPDATE";
    } else if (
      pageJsExecutedTime !== undefined &&
      pageJsExecutedTime < timeOfLastRouteChange
    ) {
      return "ROUTE_CHANGE_SUBSEQUENT";
    } else {
      return "ROUTE_CHANGE_FIRST";
    }
  } else {
    return "DIRECT_LOAD";
  }
};

function timeRelativeToTimeOrigin(
  epochTimestamp: number | undefined
): DOMHighResTimeStamp | undefined {
  const timeOrigin = getWindow()?.performance?.timeOrigin;
  if (epochTimestamp && timeOrigin) {
    return epochTimestamp - timeOrigin;
  } else {
    return undefined;
  }
}
